import React, { Fragment, useEffect, useState } from 'react';
import canUseDOM from '../../utilities/canUseDOM';
import popupAlert from '../../utilities/popup-alert';
import * as api from './WishlistApi';
import { GetWishlistResult, WishlistViewModel } from '../../types/generated';
import LoaderMask from '../../components/LoaderMask';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';
import PropTypes from 'prop-types';
import WishlistEditForm from './components/WishlistEditForm';
import { Listbox, Transition } from '@headlessui/react';
import { Button } from '../../components/Button';
import Checkbox from '../../components/Checkbox';

interface AddToMyListProps {
  isLoggedIn: boolean;
  catalogueRef?: string | null;
  dropdownTitle?: string;
  wishlists?: Array<GetWishlistResult>;
  quantity?: number;
  isBasketPage?: boolean;
  isBasketSummary?: boolean;
}

export default function AddToMyList(props: AddToMyListProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [wishlists, setWishlists] =
    useState<Array<GetWishlistResult> | null>(null);
  const [editListName, setEditListName] = useState<string>('');
  const [editListDefault, setEditListDefault] = useState<boolean>(false);
  const [currentPath, setCurrentPath] = useState<string>(
    canUseDOM() ? window.location.pathname : ''
  );
  const [selectedLists, setSelectedLists] = useState<Array<number>>([]);

  const listBoxId = `${props.catalogueRef ?? ''}-multi-select`;
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setCurrentPath(canUseDOM() ? window.location.pathname : '');
    if (!props.wishlists) {
      api.getWishlists().then((data) => {
        if (data.isNotLoggedIn) {
          setIsLoading(false);
        } else {
          setWishlists(data.getWishlistsResult);
          setIsLoading(false);
        }
      });
    }
  }, [props.wishlists]);

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditListName('');
    setEditListDefault(false);
  };

  const handleCreateInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const target = event.target;
    const name = target.name;
    if (name === 'editListName') {
      const value = target.value;
      setEditListName(value);
    }

    if (name === 'editListDefault') {
      const value = target.checked;
      setEditListDefault(value);
    }
  };

  const handleCreateList = () => {
    if (editListName) {
      api
        .createWishlist(editListName, editListDefault)
        .then((data: WishlistViewModel) => {
          setWishlists(data.allWishlists);
          setEditListName('');
          setEditListDefault(false);

          if (selectedLists?.length === 0) {
            let quantity = 1;

            if (props.quantity) {
              if (isNaN(props.quantity) || props.quantity < 1) {
                popupAlert.showNegative(
                  'Please check the quantity field, only positive numbers are allowed.'
                );
                return;
              } else {
                quantity = props.quantity;
              }
            }

            if (data.isDuplicate) {
              popupAlert.showSoftNegative(
                data.message ?? 'A list with this name already exists.'
              );
            }

            api
              .addItemToWishlist(
                props.catalogueRef || null,
                data.currentWishlistId,
                quantity
              )
              .then(() => {
                if (props.isBasketSummary) {
                  if (data.isDuplicate) {
                    popupAlert.showPositive('Basket saved to existing list ');
                  } else {
                    popupAlert.showPositive('Basket saved to new list ');
                  }
                } else {
                  if (data.isDuplicate) {
                    popupAlert.showPositive('Item added to existing list');
                  } else {
                    popupAlert.showPositive('Item added to new list');
                  }
                }
                setShowCreateModal(false);
              });
          } else if (selectedLists?.length > 0) {
            if (data.isDuplicate) {
              popupAlert.showSoftNegative(
                data.message ?? 'A list with this name already exists.'
              );
            } else if (data.isWarning) {
              popupAlert.showPositive(
                data.message ?? 'A list with this name already exists.'
              );
            } else if (data.isSuccess) {
              popupAlert.showPositive(
                data.message ?? `Successfully created my list ${editListName}`
              );
            }
            setSelectedLists([...selectedLists, data.currentWishlistId]);
            buttonRef.current?.click();
            setShowCreateModal(false);
          }
        })
        .catch((error: any) => {
          console.log(error);
          popupAlert.showNegative(
            'There was a problem completing this request, please try again.'
          );
        });
    }
  };

  const addItemToWishlists = () => {
    let quantity = 1;

    if (props.quantity) {
      if (isNaN(props.quantity) || props.quantity < 1) {
        popupAlert.showNegative(
          'Please check the quantity field, only positive numbers are allowed.'
        );
        return;
      } else {
        quantity = props.quantity;
      }
    }

    if (selectedLists && selectedLists.length > 0) {
      api
        .addItemToWishlists(props.catalogueRef || null, selectedLists, quantity)
        .then(() => {
          if (props.isBasketSummary) {
            popupAlert.showPositive(
              `Basket saved to ${selectedLists.length > 1 ? 'lists' : 'list'}`
            );
          } else {
            popupAlert.showPositive(
              `Item added to ${selectedLists.length > 1 ? 'lists' : 'list'}`
            );
          }
          setSelectedLists([]);
          buttonRef.current?.click();
        });
    }
  };

  const handleListSelect = (listId: number) => {
    const isInList = selectedLists.findIndex((x) => x == listId) !== -1;
    if (!isInList && listId) setSelectedLists([...selectedLists, listId]);
    else if (isInList && listId) {
      const index = selectedLists.indexOf(listId);
      if (index !== -1) {
        setSelectedLists([...selectedLists.filter((x) => x !== listId)]);
      }
    }
  };

  if (!props.isLoggedIn && props.isBasketSummary) {
    return (
      <div>
        <a
          style={{ whiteSpace: 'normal' }}
          className="btn btn-sm btn-outline-primary btn-block"
          href={`/login/?returnUrl=${currentPath}`}
        >
          {'Sign in to Save Basket to List'}
        </a>
      </div>
    );
  }

  if (!props.isLoggedIn) {
    return (
      <div className="add-to-my-list">
        <a
          className={`btn btn-link add-to-my-list-button ${
            props.isBasketPage ? 'pl-0' : ''
          }`}
          href={`/login/?returnUrl=${currentPath}`}
        >
          <span
            className={`add-to-list-label ${props.isBasketPage ? 'pl-0' : ''}`}
          >
            Add to My Lists
          </span>
        </a>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex'
      }}
    >
      {isLoading && (
        <LoaderMask type="absolute">
          <Loader size="xsmall" />
        </LoaderMask>
      )}
      <div className="add-to-my-list">
        <Listbox value={selectedLists} multiple>
          <Listbox.Button
            id={listBoxId}
            className="multi-select btn btn-primary"
            ref={buttonRef}
          >
            {props.dropdownTitle || 'Add to List...'}
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="options">
              <Listbox.Option
                key={'create'}
                className="create-option"
                value={listBoxId}
                onClick={() => {
                  setShowCreateModal(true);
                  setEditListDefault(wishlists?.length === 0 ?? false);
                }}
              >
                Create new list
              </Listbox.Option>
              <div className="list-items">
                {wishlists?.map((list) => (
                  <Listbox.Option
                    key={list.wishlistId}
                    className={({ active, selected }) =>
                      `option  ${active || selected ? 'active' : ''}
                    `
                    }
                    value={list.wishlistId}
                  >
                    <Checkbox
                      className="checkbox"
                      checked={
                        selectedLists.findIndex((x) => x == list.wishlistId) !==
                        -1
                      }
                      label={list.name}
                      name={list.name ?? ''}
                      value={list.wishlistId}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        event.stopPropagation();

                        if (list.wishlistId) handleListSelect(list.wishlistId);
                      }}
                    />
                  </Listbox.Option>
                ))}
              </div>

              <div className="footer">
                <div className="buttons">
                  <Button
                    handleClick={() => {
                      setSelectedLists([]);
                      buttonRef.current?.click();
                    }}
                    className="my-list-btn btn btn-outline-primary"
                  >
                    Clear
                  </Button>
                  <Button
                    handleClick={() => void addItemToWishlists()}
                    className="my-list-btn my-list-btn-add mx-2 btn btn-primary"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </Listbox.Options>
          </Transition>
        </Listbox>
      </div>
      <Modal
        title="Create a New List"
        isVisible={showCreateModal}
        handleClose={handleModalClose}
        escapeToClose={true}
        footer={
          <Footer
            isBasketPage={props.isBasketPage || false}
            handleCancel={handleModalClose}
            handleComplete={handleCreateList}
            btnText={selectedLists?.length > 0 ? 'Create' : 'Create and Add'}
          />
        }
      >
        <WishlistEditForm
          handleSubmit={handleCreateList}
          isDefaultVisible={(wishlists ?? []).length > 0}
          handleInputChange={handleCreateInputChange}
          listName={editListName}
          listIsDefault={editListDefault}
        />
      </Modal>
    </div>
  );
}

interface FooterProps {
  handleCancel: () => void;
  handleComplete: () => void;
  isBasketPage: boolean;
  btnText?: string;
}

const Footer = ({ handleCancel, handleComplete, btnText }: FooterProps) => (
  <>
    <button onClick={handleCancel} className="btn btn-outline-primary">
      Cancel
    </button>
    <button onClick={handleComplete} className="btn btn-primary">
      {btnText ?? 'Create'}
    </button>
  </>
);

Footer.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  handleComplete: PropTypes.func.isRequired,
  isBasketPage: PropTypes.bool
};
